'use client';

import ConditionalWrapper from '@/components/utility/ConditionalWrapper';
import { addError, addLog } from '@/features/datadog';
import { BackButton } from '@/ui/buttons/BackButton';
import { MediaShareButton } from '@/ui/buttons/MediaShareButton';
import { logger } from '@/utils/loggerUtils';
import { getPlayerPosterImageUrl } from '@/utils/playerUtils';
import { usePlayerProfilesContext } from '@vimond/features/video-playback/providers/PlayerProfilesProvider';
import { AssetType } from '@vimond/services/content-discovery';
import { PlaybackErrorType, PlaybackType } from '@vimond/services/play';
import { cn } from '@vimond/theme/tailwind';
import { getEnvVar } from '@vimond/utils/env';
import dynamic from 'next/dynamic';
import { memo, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { VideoInfoDetailsCard } from './VideoInfoDetailsCard';
import { VideoPlaybackErrorDisplay } from './VideoPlaybackErrorDisplay';

type Props = {
  asset: AssetType;
  startPosition: number;
  muted?: boolean;
  onPlaybackFinished?: (assetId: string) => void;
  onPlaybackPaused?: (assetId: string) => void;
  onPlaybackPositionChanges?: (assetId: string, position: number) => void;
  playback?: PlaybackType;
  playbackError?: PlaybackErrorType;
  showFullScreenVideo?: boolean;
  withVideoInfoDetails?: boolean;
};

const BitmovinPlayer = dynamic(
  () => {
    return import('../players/bitmovin')
      .then((mod) => {
        return mod.BitmovinPlayer;
      })
      .catch((error) => {
        logger.error(
          '[VideoPlayback] Failed to load BitmovinPlayer module:',
          error,
        );
        logger.error('[VideoPlayback] Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          name: error instanceof Error ? error.name : undefined,
        });
        throw error;
      });
  },
  { ssr: false },
);

const VideoJsPlayer = dynamic(
  () => {
    return import('../players/videojsPlayer/VideoJsPlayer')
      .then((mod) => {
        return mod.default;
      })
      .catch((error) => {
        logger.error(
          '[VideoPlayback] Failed to load VideoJsPlayer module:',
          error,
        );
        logger.error('[VideoPlayback] Error details:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          name: error instanceof Error ? error.name : undefined,
        });
        throw error;
      });
  },
  { ssr: false },
);

const VideoPlaybackComponent = ({
  asset,
  muted,
  onPlaybackFinished: onPlaybackFinishedProp,
  onPlaybackPositionChanges: onPlaybackPositionChangesProp,
  playback,
  playbackError,
  showFullScreenVideo = true,
  startPosition,
  withVideoInfoDetails,
}: Props) => {
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const isVerticalPlayer = getEnvVar('NEXT_PUBLIC_9_BY_16_PLAYER') === 'true';
  const shouldUseVerticalLayout = isVerticalPlayer && isSmallScreen;

  // Use shared player profiles context
  const { getAdBreaksForProfile, isLoading } = usePlayerProfilesContext();
  const adBreaks = getAdBreaksForProfile(asset.adProfile);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = /Android|webOS|iPhone|BlackBerry|IEMobile/i.test(
      window.navigator.userAgent,
    );
    const checkSmallScreen = () =>
      setIsSmallScreen(isMobile || window.innerWidth <= 476);

    checkSmallScreen();
    window.addEventListener('resize', checkSmallScreen);
    return () => window.removeEventListener('resize', checkSmallScreen);
  }, []);

  const onPlaybackFinished = useDebouncedCallback((assetId: string) => {
    if (!playback) return;
    onPlaybackFinishedProp?.(assetId);
  }, 1000);

  const onPlaybackPositionChanges = useDebouncedCallback(
    (_assetId: string, position: number) => {
      if (!playback) return;
      onPlaybackPositionChangesProp?.(_assetId, position);
    },
    1000,
    { maxWait: 5000 },
  );

  const onPlaybackPaused = useDebouncedCallback(
    (_assetId: string, position: number) => {
      if (!playback) return;
      onPlaybackPositionChangesProp?.(_assetId, position);
    },
    1000,
  );

  const poster = getPlayerPosterImageUrl({
    asset,
    verticalPlayer: isVerticalPlayer,
  });

  const useVideoJs = getEnvVar('NEXT_PUBLIC_USE_VIDEOJS_PLAYER') === 'true';
  const PlayerComponent = useVideoJs ? VideoJsPlayer : BitmovinPlayer;

  return (
    <ConditionalWrapper
      condition={!showFullScreenVideo}
      className="flex flex-col w-full"
    >
      <div
        className={cn(
          'relative flex items-center justify-center overflow-hidden',
          showFullScreenVideo && 'h-screen w-screen',
          shouldUseVerticalLayout && 'h-[65vh]',
          isLoading && 'bg-gray-900',
        )}
      >
        <div className="absolute z-10 left-3 top-5">
          <BackButton />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center w-full h-full aspect-video">
            <div className="w-12 h-12 border-b-2 border-white rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="w-full h-full aspect-video">
            <div
              className={cn(
                showFullScreenVideo
                  ? 'h-screen'
                  : 'h-full w-full custom-mini-player-ui',
                withVideoInfoDetails && 'custom-invisible-title-player',
              )}
            >
              {(() => {
                const shouldRenderPlayer = !playbackError && playback;

                if (!shouldRenderPlayer) {
                  if (!playback) {
                    logger.warn(
                      `[VideoPlayback] Not rendering player for asset ${asset.id}: playback data is missing`,
                    );
                  }
                  if (playbackError) {
                    logger.error(
                      `[VideoPlayback] Not rendering player for asset ${asset.id}: playback error present`,
                      playbackError,
                    );
                  }
                  return null;
                }

                return (
                  <PlayerComponent
                    key={playback.assetId}
                    videoKey={playback.assetId}
                    playback={{
                      ...playback,
                      assetTitle: asset.title || playback.assetTitle,
                    }}
                    muted={muted}
                    startPosition={startPosition}
                    poster={poster}
                    onPlaybackFinished={onPlaybackFinished}
                    onPlaybackPaused={onPlaybackPaused}
                    onPlaybackPositionChanges={onPlaybackPositionChanges}
                    adBreaks={adBreaks}
                    addError={addError}
                    addLog={addLog}
                    vertical={isVerticalPlayer}
                    asset={asset}
                  />
                );
              })()}
              {playbackError && (
                <VideoPlaybackErrorDisplay error={playbackError} />
              )}
            </div>
          </div>
        )}

        {!withVideoInfoDetails && (
          <div className="absolute z-10 right-5 top-5">
            {getEnvVar('NEXT_PUBLIC_ENABLE_SOCIAL_MEDIA_SHARING') !==
              'false' && (
              <MediaShareButton
                contentId={asset.id}
                contentTitle={asset.title}
              />
            )}
          </div>
        )}
      </div>

      {withVideoInfoDetails && (
        <VideoInfoDetailsCard
          contentId={asset.id}
          title={asset.title}
          description={asset['description-short'] ?? asset.description}
          publishTime={asset.publishing.start}
        />
      )}
    </ConditionalWrapper>
  );
};

VideoPlaybackComponent.displayName = 'VideoPlayback';

export const VideoPlayback = memo(VideoPlaybackComponent);
